import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Wine } from "lucide-react";
import { API, streamTokens } from "../lib/api";

export function cleanMd(text) {
  return text.replace(/\*\*/g, "").replace(/^#{1,4}\s*/gm, "").trim();
}

function getSessionId() {
  let id = localStorage.getItem("dic_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("dic_chat_session", id);
  }
  return id;
}

const GREETING = {
  role: "assistant",
  text: "Ciao! Sono l'assistente di Dentro il Calice. Dimmi pure: vini, corsi, il libro, il podcast — come posso aiutarti?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Errore");
      await streamTokens(res, (token) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + token };
          return copy;
        });
      });
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Qualcosa non ha funzionato. Scrivici a info@dentroilcalice.it e ti rispondiamo noi." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Chiedi a Stefano — apri la chat"
        className="fixed bottom-5 right-5 z-[55] h-14 w-14 rounded-full bg-bordeaux border border-oro/40 text-oro shadow-2xl flex items-center justify-center transition-transform duration-300 hover:scale-105"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[55] w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl bg-nero border border-oro/25 shadow-2xl overflow-hidden flex flex-col"
            style={{ height: "min(32rem, calc(100vh - 8rem))" }}
          >
            <div className="bg-bordeaux px-5 py-4 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-nero/40 border border-oro/40 flex items-center justify-center">
                <Wine size={16} className="text-oro" />
              </div>
              <div>
                <p className="font-serif font-semibold text-panna text-sm">Chiedi a Stefano</p>
                <p className="text-[0.65rem] text-panna/60 uppercase tracking-widest">Assistente virtuale · AI</p>
              </div>
            </div>
            <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-bordeaux text-panna rounded-br-sm"
                      : "bg-nero-light text-panna/90 border border-panna/10 rounded-bl-sm"
                  }`}>
                    {cleanMd(m.text)}
                    {m.role === "assistant" && m.text === "" && streaming && (
                      <span className="inline-flex gap-1 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-oro animate-pulse" />
                        <span className="h-1.5 w-1.5 rounded-full bg-oro animate-pulse [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-oro animate-pulse [animation-delay:300ms]" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="border-t border-panna/10 p-3 flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi la tua domanda…"
                maxLength={1000}
                className="flex-1 rounded-full bg-nero-light border border-panna/15 px-4 py-2.5 text-sm text-panna placeholder:text-panna/35 focus:outline-none focus:ring-2 focus:ring-oro/50"
              />
              <button type="submit" data-testid="chat-send" disabled={streaming || !input.trim()}
                      aria-label="Invia"
                      className="h-10 w-10 rounded-full bg-bordeaux text-panna flex items-center justify-center hover:bg-bordeaux-dark transition-colors disabled:opacity-50 shrink-0">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
